import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "react-bootstrap";
import { Checkbox, Flex, Tag } from "antd";
import { Container, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  Cancel01Icon,
  CheckmarkCircle01Icon,
  Delete02Icon,
  DeliveryReturn01Icon,
  Edit02Icon,
  ViewIcon,
} from "@hugeicons/core-free-icons";
import { PageHeader } from "@/CommonElements/PageTitle";
import { TableC } from "@/CommonElements/TableC";
import { useLocalizedText } from "@/utils/hooks/useLocalizedText";
import { DATE_TIME_FORMAT } from "@/utils/constants";
import { LabelValue } from "@/Component/Value";
import {
  useChangeNusukCardsBatchDeliveryStatusMutation,
  useDeleteNusukCardsBatchMutation,
  useGetNusukCardsBatchesQuery,
  useGetNusukCardsReturnReasonsQuery,
  usePrintNusukCardsBatchMutation,
  useReturnNusukCardsBatchMutation,
} from "@/services/nusukCards/nusukCards";
import { useDebouncedSearch } from "@/utils/hooks/useDebouncedSearch";
import { downloadFile } from "@/utils/helpers";
import { AntInput } from "@/CommonElements/AntInput";
import { AntSelect } from "@/CommonElements/AntSelect";
import { useSystem } from "@/utils/hooks/useLocation";
import { useNusukCardsPermissions } from "@/utils/hooks/useNusukCardsPermissions";
import ConfirmModal from "@/CommonElements/ConfirmModal";

const PAGE_SIZE = 10;

const EMPTY_ROWS = [];

const rowActionIcon = (icon) => <HugeiconsIcon icon={icon} size={16} />;

/** Batches marked delivered (received) — same as NUSUK_STATUS_DELIVERED in this screen. */
const isBatchDeliveredReceived = (row) => Number(row?.delivery_status) === 1;

const NusukCardsNewBatches = () => {
  const { t } = useTranslation();
  const { getLocalizedDate, getLocalizedText } = useLocalizedText();
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({});
  const { searchValue, debouncedValue, setSearchValue } = useDebouncedSearch("", 500);
  const navigate = useNavigate();
  const { currentSystem } = useSystem();
  const nusukBase = `/app/${currentSystem}/nusuk-cards`;
  const {
    canOpenBatchDetails,
    canCreateReturn,
    canChangeDeliveryStatus,
    canUpdateBatch,
    canDeleteBatch,
    canCreateBatch,
    canViewExportBatchReport,
  } = useNusukCardsPermissions();

  const apiParams = useMemo(
    () => ({
      page,
      per_page: PAGE_SIZE,
      ...(debouncedValue?.trim() ? { search: debouncedValue.trim() } : {}),
      ...filters,
    }),
    [page, debouncedValue, filters]
  );

  const { data, isLoading, isFetching, refetch } = useGetNusukCardsBatchesQuery(apiParams);
  const { mutate: printNusukCardsBatch, isPending: isPrintBatchPending } =
    usePrintNusukCardsBatchMutation();
  const { mutateAsync: returnBatch, isPending: isReturnPending } =
    useReturnNusukCardsBatchMutation();
  const { mutateAsync: changeDeliveryStatus, isPending: isDeliveryStatusPending } =
    useChangeNusukCardsBatchDeliveryStatusMutation();
  const [selectedBatchIds, setSelectedBatchIds] = useState([]);
  const [deleteModal, setDeleteModal] = useState({ open: false, batchId: null });
  const { mutateAsync: deleteNusukBatch, isPending: isDeleteBatchPending } =
    useDeleteNusukCardsBatchMutation();
  const [returnModalOpen, setReturnModalOpen] = useState(false);
  const [returnBatchId, setReturnBatchId] = useState(null);
  const [returnReasonId, setReturnReasonId] = useState(null);
  const [returnRepresentativeName, setReturnRepresentativeName] = useState("");
  const [returnErrors, setReturnErrors] = useState({});
  const {
    data: returnReasons = [],
    isLoading: returnReasonsLoading,
    isError: returnReasonsError,
  } = useGetNusukCardsReturnReasonsQuery({ enabled: returnModalOpen });
  const rows = data?.data ?? EMPTY_ROWS;
  const meta = data?.meta ?? {};
  const loading = isLoading || isFetching;

  const tableRows = useMemo(() => {
    if (!rows.length) return rows;
    return [
      ...rows,
      {
        __isTotalRow: true,
        batch_no: "",
        card_no: data?.count_of_card_numbers,
      },
    ];
  }, [rows, data?.count_of_card_numbers]);

  const lastPage = Number(meta?.last_page);

  const allPageBatchIds = useMemo(
    () => rows.map((r) => String(r.id)).filter(Boolean),
    [rows]
  );
  const selectedReceivedBatchNos = useMemo(
    () =>
      rows
        .filter(
          (r) =>
            selectedBatchIds.includes(String(r.id)) && isBatchDeliveredReceived(r)
        )
        .map((r) => String(r.batch_no))
        .filter(Boolean),
    [rows, selectedBatchIds]
  );
  const selectedNonDeliveredCount = useMemo(() => {
    const selectedRows = rows.filter((r) => selectedBatchIds.includes(String(r.id)));
    return selectedRows.filter((r) => !isBatchDeliveredReceived(r)).length;
  }, [rows, selectedBatchIds]);
  const returnReasonOptions = useMemo(
    () =>
      returnReasons
        .map((r) => {
          const id = r.id ?? r.value;
          const label =
            getLocalizedText(r.name_ar, r.name_en) ||
            r.name ||
            r.title ||
            r.reason ||
            (id != null ? String(id) : "");
          return id != null && id !== "" ? { value: id, label } : null;
        })
        .filter(Boolean),
    [returnReasons, getLocalizedText]
  );

  const allPageSelected =
    allPageBatchIds.length > 0 &&
    allPageBatchIds.every((id) => selectedBatchIds.includes(id));
  const somePageSelected =
    !allPageSelected && allPageBatchIds.some((id) => selectedBatchIds.includes(id));

  const toggleSelectAll = () => {
    if (allPageSelected) {
      setSelectedBatchIds((prev) => prev.filter((id) => !allPageBatchIds.includes(id)));
    } else {
      setSelectedBatchIds((prev) => [...new Set([...prev, ...allPageBatchIds])]);
    }
  };

  const toggleSelectRow = (batchId) => {
    const str = String(batchId);
    setSelectedBatchIds((prev) =>
      prev.includes(str) ? prev.filter((id) => id !== str) : [...prev, str]
    );
  };

  useEffect(() => {
    if (Number.isFinite(lastPage) && lastPage >= 1 && page > lastPage) {
      setPage(lastPage);
    }
  }, [lastPage, page]);

  useEffect(() => {
    setSelectedBatchIds([]);
  }, [page, debouncedValue, filters]);

  const handlePrintBatches = useCallback(
    (batchNos) => {
      if (!batchNos?.length) {
        toast.error(t("NUSUK_CARDS_BATCH_PRINT_MISSING_BATCH"));
        return;
      }
      const toastId = toast.loading(t("NUSUK_CARDS_BATCH_PRINTING"));
      printNusukCardsBatch(batchNos, {
        onSettled: () => toast.dismiss(toastId),
        onSuccess: (res) => {
          const blob = new Blob([res.data], { type: "application/pdf" });
          const label =
            batchNos.length === 1
              ? `nusuk-cards-batch-${batchNos[0]}.pdf`
              : `nusuk-cards-batches-${batchNos.length}.pdf`;
          downloadFile(blob, label);
          toast.success(t("NUSUK_CARDS_BATCH_PRINT_SUCCESS"));
        },
        onError: async (err) => {
          let message = t("NUSUK_CARDS_BATCH_PRINT_FAILED");
          const data = err?.response?.data;
          if (data instanceof Blob) {
            try {
              const parsed = JSON.parse(await data.text());
              if (parsed?.message) message = parsed.message;
            } catch {
              /* keep default */
            }
          } else if (err?.response?.data?.message) {
            message = err.response.data.message;
          }
          toast.error(message);
        },
      });
    },
    [printNusukCardsBatch, t]
  );

  const handlePrintSelectedDeliveredBatches = useCallback(() => {
    if (!selectedReceivedBatchNos.length) {
      toast.error(t("NUSUK_CARDS_BATCH_PRINT_ONLY_RECEIVED"));
      return;
    }
    if (selectedNonDeliveredCount > 0) {
      toast.info(
        t("NUSUK_CARDS_BATCH_PRINT_OMITTED_NOT_DELIVERED", {
          count: selectedNonDeliveredCount,
        })
      );
    }
    handlePrintBatches(selectedReceivedBatchNos);
  }, [
    selectedReceivedBatchNos,
    selectedNonDeliveredCount,
    handlePrintBatches,
    t,
  ]);

  const columns = [
      ...(canViewExportBatchReport
        ? [
            {
              key: "select",
              name: (
                <Checkbox
                  checked={allPageSelected}
                  indeterminate={somePageSelected}
                  onChange={toggleSelectAll}
                />
              ),
              filterable: false,
              render: (_, row) => {
                if (row?.__isTotalRow) return null;
                const batchId = String(row?.id);
                return (
                  <Checkbox
                    checked={selectedBatchIds.includes(batchId)}
                    onChange={() => toggleSelectRow(batchId)}
                  />
                );
              },
            },
          ]
        : []),
      {
        key: "batch_no",
        name: t("NUSUK_BATCH_NUMBER"),
        render: (_, row) => row?.batch_no ?? "-",
      },
      {
        key: "campaign_id",
        name: t("CAMPAIGN_OR_GROUP_NUMBER"),
        render: (_, row) => {
          const number = row?.entity_details?.number ?? row?.campaign_id;
          return (
            <LabelValue
              value={number ?? "-"}
              copyValue={number}
              buttonIconSize={12}
            />
          );
        },
      },
      {
        key: "service_center",
        name: t("SERVICE_CENTER"),
        render: (_, row) =>
          getLocalizedText(
            row?.entity_details?.service_center?.name_ar,
            row?.entity_details?.service_center?.name_en
          ) ||
          row?.entity_details?.service_center?.name ||
          "-",
      },
      {
        key: "nationality",
        name: t("NATIONALITY"),
        render: (_, row) =>
          getLocalizedText(
            row?.entity_details?.nationality?.name_ar,
            row?.entity_details?.nationality?.name_en
          ) ||
          row?.entity_details?.nationality?.name ||
          "-",
      },
      {
        key: "group_name",
        name: t("GROUP_NAME"),
        render: (_, row) =>
          row?.__isTotalRow
            ? ""
            : row?.group_name || row?.entity_details?.name || "-",
      },
      {
        key: "mapped_group_name",
        name: t("MAPPED_GROUP_NAME"),
        render: (_, row) =>
          row?.__isTotalRow
            ? ""
            : row?.mapped_group_name ??
              row?.entity_details?.mapped_group_name ??
              "-",
      },
      {
        key: "card_no",
        name: t("NUSUK_CARDS_COUNT"),
        render: (_, row) =>
          row?.__isTotalRow ? (
            <strong>{row?.card_no ?? 0}</strong>
          ) : (
            row?.card_no ?? "-"
          ),
      },
      // رقم الخزينة
      {
        key: "safe_number",
        name: t("NUSUK_SAFE_NUMBER"),
        render: (_, row) => row?.safe_number ?? "-",
      },
      {
        key: "added_at",
        name: t("NUSUK_BATCH_DATETIME"),
        render: (_, row) =>
          row?.added_at ? getLocalizedDate(row.added_at, DATE_TIME_FORMAT) : "-",
      },
      {
        key: "delivery_status",
        name: t("STATUS"),
        render: (_, row) => {
          if (Number(row?.delivery_status) === 1) {
            return <Tag color="success">{t("NUSUK_STATUS_DELIVERED")}</Tag>;
          }
          if (Number(row?.delivery_status) === 0) {
            return <Tag color="error">{t("NUSUK_STATUS_NOT_RECEIVED")}</Tag>;
          }
          if (Number(row?.delivery_status) === 2) {
            return <Tag color="warning">{t("NUSUK_STATUS_RETURNED")}</Tag>;
          }
          return "-";
        },
      },
      {
        key: "delivery_date",
        name: t("NUSUK_DELIVERY_DATE"),
        render: (_, row) =>
          row?.delivery_date ? getLocalizedDate(row.delivery_date, DATE_TIME_FORMAT) : "-",
      },
      {
        key: "actions",
        name: t("ACTIONS"),
        filterable: false,
      },
    ]

  const filterFields = useMemo(
    () => [
      { name: "batch_number", label: t("NUSUK_BATCH_NUMBER"), type: "input" },
      {
        name: "campaign_group_number",
        label: t("CAMPAIGN_OR_GROUP_NUMBER"),
        type: "input",
        placeholder: t("CAMPAIGN_NUMBER_PLACEHOLDER"),
      },
      {
        name: "service_center_id",
        label: t("SERVICE_CENTER"),
        type: "select",
        optionKey: "service_centers",
        options: (s) => ({ value: s.id, label: s.name }),
      },
      {
        name: "nationality_id",
        label: t("NATIONALITY"),
        type: "select",
        optionKey: "nationalities",
        options: (n) => ({
          value: n.id,
          label: getLocalizedText(n.name_ar, n.name_en) || n.name,
        }),
      },
      {
        name: "group_name",
        label: t("GROUP_NAME"),
        type: "input",
      },
      { name: "batch_date", label: t("NUSUK_BATCH_DATETIME"), type: "date" },
      {
        name: "delivery_status",
        label: t("STATUS"),
        type: "select",
        options: [
          { value: "1", label: t("NUSUK_STATUS_DELIVERED") },
          { value: "0", label: t("NUSUK_STATUS_NOT_RECEIVED") },
          { value: "2", label: t("NUSUK_STATUS_RETURNED") },
        ],
      },
      { name: "request_number", label: t("ARRIVAL_CARD_REQUEST_NUMBER"), type: "input" },
    ],
    [t, getLocalizedText]
  );

  const getRowActions = useCallback(
    (row) => {
      if (row?.__isTotalRow) return [];
      const delivered = Number(row?.delivery_status) === 1;
      const notReceived = Number(row?.delivery_status) === 0;
      const batchId = row?.id;

      const actions = [];
      if (canOpenBatchDetails) {
        actions.push({
          key: "view",
          icon: rowActionIcon(ViewIcon),
          label: t("VIEW"),
          onClick: () => navigate(`${nusukBase}/${row?.id}/view`),
        });
      }
      if (canCreateReturn) {
        actions.push({
          key: "return",
          icon: rowActionIcon(DeliveryReturn01Icon),
          label: t("NUSUK_CARDS_RETURNS"),
          disabled: isReturnPending || isDeliveryStatusPending,
          onClick: () => openReturnModal(batchId),
        });
      }
      if (canChangeDeliveryStatus) {
        actions.push({
          key: "received",
          icon: rowActionIcon(CheckmarkCircle01Icon),
          label: t("NUSUK_CARDS_RECEIVED"),
          disabled: delivered || isReturnPending || isDeliveryStatusPending,
          onClick: async () => {
            if (!batchId) {
              toast.error(t("SOMETHING_WENT_WRONG"));
              return;
            }
            const toastId = toast.loading(t("LOADING"));
            try {
              await changeDeliveryStatus({ batchId, delivery_status: 1 });
              toast.dismiss(toastId);
              toast.success(t("CHANGES_SAVED_SUCCESSFULLY"));
              refetch();
            } catch (err) {
              toast.dismiss(toastId);
              toast.error(err?.response?.data?.message || t("SOMETHING_WENT_WRONG"));
            }
          },
        });
      }
      if (canChangeDeliveryStatus) {
        actions.push({
          key: "not_received",
          icon: rowActionIcon(Cancel01Icon),
          label: t("NUSUK_CARDS_NOT_RECEIVED"),
          disabled: notReceived || isReturnPending || isDeliveryStatusPending,
          onClick: async () => {
            if (!batchId) {
              toast.error(t("SOMETHING_WENT_WRONG"));
              return;
            }
            const toastId = toast.loading(t("LOADING"));
            try {
              await changeDeliveryStatus({ batchId, delivery_status: 0 });
              toast.dismiss(toastId);
              toast.success(t("CHANGES_SAVED_SUCCESSFULLY"));
              refetch();
            } catch (err) {
              toast.dismiss(toastId);
              toast.error(err?.response?.data?.message || t("SOMETHING_WENT_WRONG"));
            }
          },
        });
      }
      if (canUpdateBatch) {
        actions.push({
          key: "edit",
          icon: rowActionIcon(Edit02Icon),
          label: t("EDIT"),
          disabled: delivered,
          onClick: () => navigate(`${nusukBase}/${row?.id}/edit`),
        });
      }
      if (canDeleteBatch) {
        actions.push({
          key: "delete",
          icon: rowActionIcon(Delete02Icon),
          label: t("DELETE"),
          danger: true,
          onClick: () => {
            if (!batchId) {
              toast.error(t("SOMETHING_WENT_WRONG"));
              return;
            }
            setDeleteModal({ open: true, batchId });
          },
        });
      }
      // TableC only uses ActionDropdown when length > 1; a single action is otherwise non-clickable.
      if (actions.length === 1) {
        actions.push({ type: "divider", key: "nusuk_batch_actions_divider" });
      }
      return actions;
    },
    [
      t,
      navigate,
      nusukBase,
      isReturnPending,
      isDeliveryStatusPending,
      changeDeliveryStatus,
      refetch,
      canOpenBatchDetails,
      canCreateReturn,
      canChangeDeliveryStatus,
      canUpdateBatch,
      canDeleteBatch,
      setDeleteModal,
    ]
  );

  const handleFilterApply = (next) => {
    setFilters(next);
    setPage(1);
  };
  const handleFilterReset = () => {
    setFilters({});
    setPage(1);
  };

  const handleReceiveNewBatch = () => {
    navigate(`${nusukBase}/enter-new-received-new-batches-data`);
  };

  const openReturnModal = (batchId) => {
    setReturnBatchId(batchId);
    setReturnReasonId(null);
    setReturnRepresentativeName("");
    setReturnErrors({});
    setReturnModalOpen(true);
  };

  const closeReturnModal = () => {
    setReturnModalOpen(false);
    setReturnBatchId(null);
    setReturnReasonId(null);
    setReturnRepresentativeName("");
    setReturnErrors({});
  };

  const confirmReturn = async () => {
    const representative_name = returnRepresentativeName.trim();
    const nextErrors = {};
    if (returnReasonId == null || returnReasonId === "") {
      nextErrors.reason = t("FIELD_REQUIRED");
    }
    if (!representative_name) nextErrors.representative_name = t("FIELD_REQUIRED");
    setReturnErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    if (!returnBatchId) {
      toast.error(t("SOMETHING_WENT_WRONG"));
      return;
    }

    const toastId = toast.loading(t("LOADING"));
    try {
      await returnBatch({
        batchId: returnBatchId,
        payload: {
          reason_id: returnReasonId,
          representative_name,
        },
      });
      toast.dismiss(toastId);
      toast.success(t("CHANGES_SAVED_SUCCESSFULLY"));
      closeReturnModal();
      refetch();
    } catch (err) {
      toast.dismiss(toastId);
      toast.error(err?.response?.data?.message || t("SOMETHING_WENT_WRONG"));
    }
  };

  return (
    <div>
      <PageHeader title={t("NUSUK_CARDS_NEW_BATCHES_SCREEN")} sticky={false} />
      <Container fluid>
        <TableC
          data={tableRows}
          columns={columns}
          loading={loading}
          searchValue={searchValue}
          onSearchChange={(e) => {
            setSearchValue(e.target.value);
            setPage(1);
          }}
          searchPlaceholder={t("SEARCH_HERE")}
          pagination={{
            current: meta?.current_page ?? page,
            total: meta?.total ?? 0,
            pageSize: meta?.per_page ?? PAGE_SIZE,
            onChange: setPage,
          }}
          filterFields={filterFields}
          filters={filters}
          onFilterApply={handleFilterApply}
          onFilterReset={handleFilterReset}
          actions={getRowActions}
          getRowStyle={(row) =>
            row?.__isTotalRow
              ? { backgroundColor: "#fafafa", fontWeight: 600 }
              : {}
          }
          renderActions={
            <Flex gap={8}>
              {selectedBatchIds.length > 0 && canViewExportBatchReport && (
                <Button
                  className="btn-success btn-sm"
                  type="button"
                  disabled={
                    isPrintBatchPending || selectedReceivedBatchNos.length === 0
                  }
                  title={
                    selectedReceivedBatchNos.length === 0
                      ? t("NUSUK_CARDS_BATCH_PRINT_ONLY_RECEIVED")
                      : undefined
                  }
                  onClick={handlePrintSelectedDeliveredBatches}
                >
                  {t("NUSUK_PRINT_SELECTED_DELIVERED_BATCHES", {
                    count: selectedReceivedBatchNos.length,
                  })}
                </Button>
              )}
              {canCreateBatch && (
                <Button className="btn-primary btn-sm" type="button" onClick={handleReceiveNewBatch}>
                  {t("NUSUK_RECEIVE_NEW_BATCH")}
                </Button>
              )}
            </Flex>
          }
        />
      </Container>
      <Modal isOpen={returnModalOpen} toggle={closeReturnModal} centered>
        <ModalHeader toggle={closeReturnModal}>{t("NUSUK_CARDS_RETURNS")}</ModalHeader>
        <ModalBody>
          <div className="mb-3">
            <label className="form-label fw-bold required">{t("NUSUK_REPRESENTATIVE_NAME")}</label>
            <AntInput
              value={returnRepresentativeName}
              onChange={(e) => {
                setReturnRepresentativeName(e.target.value);
                if (returnErrors.representative_name) {
                  setReturnErrors((prev) => ({ ...prev, representative_name: undefined }));
                }
              }}
              placeholder={t("NUSUK_REPRESENTATIVE_NAME_PLACEHOLDER")}
              status={returnErrors.representative_name ? "error" : undefined}
            />
            {returnErrors.representative_name && (
              <div className="text-danger mt-1">{returnErrors.representative_name}</div>
            )}
          </div>
          <div>
            <label className="form-label fw-bold required">{t("RESOLUTION_REASON")}</label>
            {returnReasonsError ? (
              <p className="text-danger small mb-0">{t("SOMETHING_WENT_WRONG")}</p>
            ) : (
              <AntSelect
                loading={returnReasonsLoading}
                options={returnReasonOptions}
                value={returnReasonId ?? undefined}
                onChange={(val) => {
                  setReturnReasonId(val ?? null);
                  if (returnErrors.reason) {
                    setReturnErrors((prev) => ({ ...prev, reason: undefined }));
                  }
                }}
                placeholder={t("NUSUK_RETURN_REASON_PLACEHOLDER")}
                showSearch
                optionFilterProp="label"
                status={returnErrors.reason ? "error" : undefined}
              />
            )}
            {returnErrors.reason && <div className="text-danger mt-1">{returnErrors.reason}</div>}
          </div>
        </ModalBody>
        <ModalFooter>
          <Button className="btn btn-light btn-sm" type="button" onClick={closeReturnModal}>
            {t("CANCEL")}
          </Button>
          <Button
            className="btn btn-primary btn-sm"
            type="button"
            disabled={isReturnPending || returnReasonsLoading || returnReasonsError}
            onClick={confirmReturn}
          >
            {t("CONFIRM")}
          </Button>
        </ModalFooter>
      </Modal>

      <ConfirmModal
        isOpen={deleteModal.open}
        onToggle={() =>
          !isDeleteBatchPending && setDeleteModal({ open: false, batchId: null })
        }
        title={t("DELETE")}
        message={t("NUSUK_BATCH_DELETE_CONFIRM")}
        isLoading={isDeleteBatchPending}
        onConfirm={async () => {
          const id = deleteModal.batchId;
          if (!id) return;
          try {
            await deleteNusukBatch(id);
            setDeleteModal({ open: false, batchId: null });
          } catch {
            /* toast handled in mutation */
          }
        }}
      />
    </div>
  );
};

export default NusukCardsNewBatches;
